import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { loadStripe, Stripe, StripeCardElement } from '@stripe/stripe-js';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fetchAppConfig } from '../../services/appConfigService';
import {
  BankAccount,
  PaymentCard,
  StripeConnectAccount,
  StripeExternalPayoutAccount,
  StripePaymentMethod,
  createExternalPayoutAccount,
  createHelperExpressDashboardLink,
  createHelperExpressOnboardingLink,
  createStripePaymentMethod,
  deleteExternalPayoutAccount,
  deletePaymentMethod,
  fetchBankAccounts,
  fetchPaymentCards,
  fetchStripeConnectAccount,
  fetchStripePaymentMethods,
  setDefaultExternalPayoutAccount,
  setDefaultOutgoingPaymentMethod,
} from '../../services/paymentService';
import { methodLabel } from '../../utils/paymentFormatters';
import '../../styles/AccountTools.css';

type Message = { type: 'success' | 'error'; text: string } | null;
type ModalMode = 'payment' | 'payout' | null;

const Wallet = () => {
  const { user } = useAuth();
  const actorId = user?.id || '';
  const isHelper = String(user?.currentAppRole || '').toUpperCase() === 'HELPER';

  const [stripePaymentMethods, setStripePaymentMethods] = useState<StripePaymentMethod[]>([]);
  const [paymentCards, setPaymentCards] = useState<PaymentCard[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [connectAccount, setConnectAccount] = useState<StripeConnectAccount | null>(null);
  const [externalAccounts, setExternalAccounts] = useState<StripeExternalPayoutAccount[]>([]);
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [helperStripe, setHelperStripe] = useState<Stripe | null>(null);
  const [stripeKey, setStripeKey] = useState('');
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [cardElement, setCardElement] = useState<StripeCardElement | null>(null);
  const [message, setMessage] = useState<Message>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const cardMountRef = useRef<HTMLDivElement>(null);

  const showMessage = (next: Message) => {
    setMessage(next);
    if (next) window.setTimeout(() => setMessage(null), 4000);
  };

  const loadStripeConfig = useCallback(async () => {
    const config = await fetchAppConfig();
    const publishableKey = config.stripePublishableKey || config.stripPublishableKey || '';
    setStripeKey(publishableKey);
    if (publishableKey) {
      const stripeInstance = await loadStripe(publishableKey);
      setStripe(stripeInstance);
    }
  }, []);

  const loadWallet = useCallback(async () => {
    if (!actorId) return;
    setLoading(true);
    try {
      const [stripeMethods, cards, accounts, connect] = await Promise.all([
        fetchStripePaymentMethods(actorId),
        fetchPaymentCards(actorId),
        fetchBankAccounts(actorId),
        isHelper ? fetchStripeConnectAccount(actorId, true).catch(() => null) : Promise.resolve(null),
      ]);

      setStripePaymentMethods(stripeMethods);
      setPaymentCards(cards);
      setBankAccounts(accounts);
      setConnectAccount(connect);
      setExternalAccounts(connect?.externalAccounts || []);
    } catch (error) {
      showMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to load wallet.' });
    } finally {
      setLoading(false);
    }
  }, [actorId, isHelper]);

  useEffect(() => {
    loadStripeConfig();
  }, [loadStripeConfig]);

  useEffect(() => {
    loadWallet();
  }, [loadWallet]);

  useEffect(() => {
    if (!stripeKey || !connectAccount?.stripeAccountId) {
      setHelperStripe(null);
      return;
    }

    loadStripe(stripeKey, { stripeAccount: connectAccount.stripeAccountId }).then(setHelperStripe);
  }, [connectAccount?.stripeAccountId, stripeKey]);

  useEffect(() => {
    if (!modalMode || !cardMountRef.current) return undefined;
    const activeStripe = modalMode === 'payout' ? helperStripe : stripe;
    if (!activeStripe) return undefined;

    const elements = activeStripe.elements();
    const card = elements.create('card', {
      style: {
        base: {
          fontSize: '16px',
          color: '#1f2937',
          '::placeholder': { color: '#8a94a6' },
        },
        invalid: { color: '#dc2626' },
      },
    });
    card.mount(cardMountRef.current);
    setCardElement(card);

    return () => {
      card.unmount();
      setCardElement(null);
    };
  }, [helperStripe, modalMode, stripe]);

  const closeModal = () => {
    if (saving) return;
    setModalMode(null);
  };

  const saveRequesterCard = async () => {
    if (!stripe || !cardElement || !actorId) return;
    setSaving(true);
    try {
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'Requester',
          email: user?.email || undefined,
        },
      });

      if (error || !paymentMethod?.id) {
        throw new Error(error?.message || 'Unable to create Stripe payment method.');
      }

      await createStripePaymentMethod(paymentMethod.id, actorId);
      setModalMode(null);
      await loadWallet();
      showMessage({ type: 'success', text: 'Payment method saved.' });
    } catch (error) {
      showMessage({ type: 'error', text: error instanceof Error ? error.message : 'Unable to save payment method.' });
    } finally {
      setSaving(false);
    }
  };

  const savePayoutCard = async () => {
    if (!helperStripe || !cardElement || !actorId) return;
    setSaving(true);
    try {
      const { token, error } = await helperStripe.createToken(cardElement);
      if (error || !token?.id) throw new Error(error?.message || 'Unable to create payout token.');

      await createExternalPayoutAccount({ externalAccountToken: token.id }, actorId);
      setModalMode(null);
      await loadWallet();
      showMessage({ type: 'success', text: 'Payout card added.' });
    } catch (error) {
      showMessage({ type: 'error', text: error instanceof Error ? error.message : 'Unable to add payout account.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDefaultOutgoing = async (methodId: string) => {
    setSaving(true);
    try {
      await setDefaultOutgoingPaymentMethod(methodId, actorId);
      await loadWallet();
      showMessage({ type: 'success', text: 'Default outgoing payment method updated.' });
    } catch (error) {
      showMessage({ type: 'error', text: error instanceof Error ? error.message : 'Unable to update default method.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePaymentMethod = async (methodId: string) => {
    if (!window.confirm('Delete this payment method?')) return;
    setSaving(true);
    try {
      await deletePaymentMethod(methodId, actorId);
      await loadWallet();
      showMessage({ type: 'success', text: 'Payment method deleted.' });
    } catch (error) {
      showMessage({ type: 'error', text: error instanceof Error ? error.message : 'Unable to delete payment method.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDefaultPayout = async (accountId: string) => {
    setSaving(true);
    try {
      await setDefaultExternalPayoutAccount(accountId, actorId);
      await loadWallet();
      showMessage({ type: 'success', text: 'Default payout account updated.' });
    } catch (error) {
      showMessage({ type: 'error', text: error instanceof Error ? error.message : 'Unable to update payout account.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePayout = async (accountId: string) => {
    if (!window.confirm('Delete this payout account?')) return;
    setSaving(true);
    try {
      await deleteExternalPayoutAccount(accountId, actorId);
      await loadWallet();
      showMessage({ type: 'success', text: 'Payout account deleted.' });
    } catch (error) {
      showMessage({ type: 'error', text: error instanceof Error ? error.message : 'Unable to delete payout account.' });
    } finally {
      setSaving(false);
    }
  };

  const openConnectLink = async (kind: 'onboard' | 'dashboard') => {
    if (!actorId) return;
    setSaving(true);
    try {
      const response =
        kind === 'onboard'
          ? await createHelperExpressOnboardingLink(actorId, actorId)
          : await createHelperExpressDashboardLink(actorId, actorId);
      window.open(response.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      showMessage({ type: 'error', text: error instanceof Error ? error.message : 'Unable to open Stripe link.' });
    } finally {
      setSaving(false);
    }
  };

  const outgoingMethods = useMemo(
    () => [
      ...stripePaymentMethods.map((method) => ({
        id: method.id,
        label: methodLabel(method),
        default: !!method.defaultOutgoingPaymentMethod,
        type: 'Stripe',
      })),
      ...paymentCards.map((method) => ({
        id: method.id,
        label: methodLabel(method),
        default: !!method.defaultOutgoingPaymentMethod,
        type: method.cardType || 'Card',
      })),
      ...bankAccounts.map((method) => ({
        id: method.id,
        label: methodLabel(method),
        default: !!method.defaultOutgoingPaymentMethod,
        type: 'Bank',
      })),
    ],
    [bankAccounts, paymentCards, stripePaymentMethods]
  );

  if (!actorId) {
    return (
      <main className="tool-page">
        <div className="tool-card tool-empty">Sign in to manage payment methods.</div>
      </main>
    );
  }

  return (
    <main className="tool-page">
      <div className="tool-header">
        <div>
          <p className="tool-breadcrumb"><Link to="/account/payments">Payments &amp; Credits</Link> / Wallet</p>
          <h1>Wallet</h1>
          <p>{isHelper ? 'Your payout methods — where earnings go.' : 'Your payment methods — how you pay.'}</p>
        </div>
        <div className="tool-actions">
          <button className="tool-btn-secondary" onClick={loadWallet} disabled={loading || saving}>
            Refresh
          </button>
          {!isHelper && (
            <button className="tool-btn" onClick={() => setModalMode('payment')} disabled={!stripe || saving}>
              Add Payment Method
            </button>
          )}
          {isHelper && (
            <button className="tool-btn" onClick={() => setModalMode('payout')} disabled={!helperStripe || saving}>
              Add Payout Card
            </button>
          )}
        </div>
      </div>

      {message && <div className={`tool-message ${message.type}`}>{message.text}</div>}

      {loading ? (
        <div className="tool-loading">Loading wallet...</div>
      ) : (
        <div className="tool-grid" style={{ marginTop: 18 }}>
          <section className="tool-card">
            <h2>{isHelper ? 'Receiving Payment Methods' : 'Issuing Payment Methods'}</h2>
            <div className="tool-list">
              {outgoingMethods.length === 0 ? (
                <div className="tool-empty">No outgoing payment methods yet.</div>
              ) : (
                outgoingMethods.map((method) => (
                  <div className="tool-row" key={`${method.type}-${method.id}`}>
                    <div className="tool-row-main">
                      <div className="tool-row-title">{method.label}</div>
                      <div className="tool-row-sub">{method.type}</div>
                    </div>
                    <div className="tool-inline">
                      {method.default ? <span className="tool-pill">Default</span> : null}
                      {!method.default && (
                        <button className="tool-btn-secondary" onClick={() => handleDefaultOutgoing(method.id)} disabled={saving}>
                          Set default
                        </button>
                      )}
                      <button className="tool-btn-danger" onClick={() => handleDeletePaymentMethod(method.id)} disabled={saving}>
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>

          <section className="tool-card">
            <h2>Helper Stripe Connect</h2>
            {!isHelper ? (
              <p>Switch to a helper role in your profile to manage receiving payment methods.</p>
            ) : (
              <>
                <div className="tool-row">
                  <div className="tool-row-main">
                    <div className="tool-row-title">{connectAccount?.stripeAccountId || 'Stripe account not ready'}</div>
                    <div className="tool-row-sub">
                      Status: {connectAccount?.status || 'Not available'}.
                      {' '}
                      Payouts: {connectAccount?.payoutsEnabled ? 'enabled' : 'not enabled'}.
                    </div>
                  </div>
                  <div className="tool-inline">
                    <button className="tool-btn-secondary" onClick={() => openConnectLink('onboard')} disabled={saving}>
                      Onboard
                    </button>
                    <button className="tool-btn-secondary" onClick={() => openConnectLink('dashboard')} disabled={saving}>
                      Dashboard
                    </button>
                  </div>
                </div>
                <div className="tool-list" style={{ marginTop: 12 }}>
                  {externalAccounts.length === 0 ? (
                    <div className="tool-empty">No payout accounts yet.</div>
                  ) : (
                    externalAccounts.map((account) => {
                      const isDefault = !!(account.isDefault || account.isDefaultPaymentAccount);
                      return (
                        <div className="tool-row" key={account.id}>
                          <div className="tool-row-main">
                            <div className="tool-row-title">{methodLabel(account)}</div>
                            <div className="tool-row-sub">
                              {[account.country, account.currency].filter(Boolean).join(' / ') || 'Payout account'}
                            </div>
                          </div>
                          <div className="tool-inline">
                            {isDefault ? <span className="tool-pill">Default</span> : null}
                            {!isDefault && (
                              <button className="tool-btn-secondary" onClick={() => handleDefaultPayout(account.id)} disabled={saving}>
                                Set default
                              </button>
                            )}
                            <button className="tool-btn-danger" onClick={() => handleDeletePayout(account.id)} disabled={saving}>
                              Delete
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </section>
        </div>
      )}

      {modalMode && (
        <div className="tool-modal" role="dialog" aria-modal="true">
          <div className="tool-modal-card">
            <div className="tool-modal-head">
              <div>
                <h3>{modalMode === 'payout' ? 'Add Payout Card' : 'Add Payment Method'}</h3>
                <p>Card details are tokenized by Stripe before the backend receives the token.</p>
              </div>
              <button className="tool-close" type="button" onClick={closeModal}>x</button>
            </div>
            <div className="tool-form">
              <div className="tool-field">
                <label>Card Details</label>
                <div ref={cardMountRef} className="stripe-card-box" />
              </div>
              <div className="tool-inline">
                <button className="tool-btn-secondary" onClick={closeModal} disabled={saving}>
                  Cancel
                </button>
                <button
                  className="tool-btn"
                  onClick={modalMode === 'payout' ? savePayoutCard : saveRequesterCard}
                  disabled={saving || !cardElement}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default Wallet;
